import { createClient, type SupabaseClient } from '@supabase/supabase-js'

import { extractSupabaseAccessToken } from './bridge-auth'
import { appEnv } from './env'
import { createBridgeSupabaseFetch, setBridgeTokenRefresher } from './supabase-auth-token'
import { isViewAsSessionError, VIEW_AS_EXPIRED_EVENT, withViewAsHeader } from './view-as'

/**
 * Cliente Supabase do Supply — schema dedicado `supply`.
 *
 * O JWT vem do bridge (produção) ou do login local (dev). Anon key no browser;
 * a autorização real é JWT + RLS.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupplyClient = SupabaseClient<any, 'supply', any>

let client: SupplyClient | null = null

/**
 * O `supabase-js` exige URL ABSOLUTA (`new URL(url)` por dentro); o build usa
 * `/supabase` same-origin. Resolver contra a origem da página mantém o bundle
 * sem domínio fixo — lição do primeiro acesso do Hub SISTEMAS em produção.
 */
export function resolveSupabaseUrl(raw: string, origin: string): string {
  return new URL(raw, origin).toString().replace(/\/$/, '')
}

export function getSupabase(): SupplyClient {
  if (!client) {
    client = createClient(resolveSupabaseUrl(appEnv.supabaseUrl, window.location.origin), appEnv.supabaseAnonKey, {
      auth: {
        persistSession: appEnv.authMode === 'local',
        autoRefreshToken: appEnv.authMode === 'local',
        detectSessionInUrl: false,
        storageKey: 'supply-auth',
      },
      db: { schema: 'supply' },
      // Sobrescreve o Authorization com o token do bridge e, num 401 (token
      // expirado em aba aberta há muito tempo), pede um novo ao bridge e
      // reenvia uma vez — em vez de derrubar a pessoa para o login (PMC).
      // "Ver como": o header da sessão de visualização vai junto (view-as.ts).
      global: { fetch: createBridgeSupabaseFetch(withViewAsHeader()) },
    }) as SupplyClient
  }
  return client
}

setBridgeTokenRefresher(async () => {
  try {
    const response = await fetch(`${appEnv.bridgeBaseUrl}/api/session`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return undefined
    const payload = (await response.json()) as Record<string, unknown>
    return extractSupabaseAccessToken(payload) ?? undefined
  } catch {
    return undefined
  }
})

export function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const { status, code, message = '' } = error as { status?: number; code?: string; message?: string }
  return status === 401 || code === 'PGRST301' || /jwt|unauthorized|not authenticated/i.test(message)
}

/** Servidor inalcançável (rede, VPN, dev01 fora) — diferente de "não autorizado". */
export function isUnreachableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const { status, message = '' } = error as { status?: number; message?: string }
  return (
    status === 0 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504 ||
    /failed to fetch|networkerror|load failed|etimedout|econnrefused|timeout/i.test(message)
  )
}

/**
 * Mensagem de erro de banco pronta para a tela: sem rede, sem sessão, ou a
 * recusa real do Postgres (normalmente uma regra de negócio falando em pt-BR,
 * vinda dos triggers do schema `supply`).
 */
export function describeDbError(error: unknown, fallback: string): string {
  if (isUnreachableError(error)) {
    return appEnv.authMode === 'local'
      ? 'Sem conexão com o Supabase local. Rode `pnpm db:start` e tente de novo.'
      : 'Sem conexão com o servidor. Verifique a rede ou a VPN e tente de novo.'
  }
  if (isUnauthorizedError(error)) {
    return 'Sua sessão expirou. Recarregue a página para entrar de novo.'
  }
  if (isViewAsSessionError(error)) {
    // A aba sai do modo e volta à visão do gestor (ViewAsBanner escuta).
    window.dispatchEvent(new Event(VIEW_AS_EXPIRED_EVENT))
  }
  const e = error as { message?: string; code?: string } | null
  if (e?.code === '42501') {
    return e.message && !/permission denied|row-level security/i.test(e.message)
      ? e.message
      : 'Você não tem permissão para esta operação (só a área da tarefa ou o gestor).'
  }
  return e?.message?.trim() || fallback
}
